import { useMemo } from 'react';

import type { ColGroupDef } from 'ag-grid-enterprise';

import type { MailingSummary } from '@enterprise/api';

import type { ListSegmentCellProps } from '@components/grid/renderers/types/list-segment-renderer.params';
import type { PercentCellProps } from '@components/grid/renderers/types/percent-cell-renderer.params';

import DateCellRenderer from '@components/grid/renderers/date-cell.renderer';
import ListSegmentRenderer from '@components/grid/renderers/list-segment-cell-renderer';
import NumberCellRenderer from '@components/grid/renderers/number-cell.renderer';
import PercentCellRenderer from '@components/grid/renderers/percent-cell.renderer';

export const useGetMailingSummaryColumns = () => {
  return useMemo(() => {
    return [
      {
        children: [
          {
            field: 'mailingId',
            headerName: 'Mailing ID',
            hide: true,
            type: ['numericColumn'],
            width: 175,
          },
          {
            cellDataType: 'date',
            cellRenderer: DateCellRenderer,
            field: 'mailDate',
            headerName: 'Mail Date',
          },
          {
            field: 'mailCode',
            headerName: 'MailCode',
            width: 150,
          },
          {
            cellRenderer: ListSegmentRenderer,
            cellRendererParams: (params: ListSegmentCellProps<MailingSummary>) => {
              return {
                ...params,
                header: 'listDisplayName',
                subHeader: 'listManagerName',
              } as ListSegmentCellProps<MailingSummary>;
            },
            field: 'listDisplayName',
            headerName: 'List/Segment',
            wrapText: true,
          },
          {
            aggFunc: 'sum',
            cellRenderer: NumberCellRenderer,
            field: 'quantityOrdered',
            headerName: 'Requested',
            type: ['numericColumn'],
            width: 145,
          },
          {
            aggFunc: 'sum',
            cellRenderer: NumberCellRenderer,
            cellStyle: { fontWeight: 'bold' },
            field: 'quantityReceived',
            headerName: 'Received',
            type: ['numericColumn'],
            width: 145,
          },
        ],
        headerClass: ['bg-blue-950'],
        headerName: 'Mailing and List Information',
      },
      {
        children: [
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'quantitySuppressed',
            headerClass: ['bg-yellow-900'],
            headerName: 'Suppress',
            type: ['numericColumn'],
            width: 200,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-100', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'quantityDupes',
            headerClass: ['bg-yellow-900'],
            headerName: 'Dupe',
            type: ['numericColumn'],
            width: 145,
          },
        ],
        headerClass: ['bg-yellow-950'],
        headerGroupComponentParams: {
          icon: 'fa-user-minus',
        },
        headerName: 'Streamline',
      },
      {
        children: [
          {
            aggFunc: 'sum',
            cellClass: ['bg-gray-200', 'text-gray-900'],
            cellRenderer: NumberCellRenderer,
            field: 'quantityFirstOutput',
            headerClass: ['bg-gray-800'],
            headerName: 'First Output',
            type: ['numericColumn'],
            width: 160,
          },
        ],
        headerClass: ['bg-gray-900'],
        headerName: '',
      },
      {
        children: [
          {
            aggFunc: 'sum',
            cellClass: ['bg-teal-300', 'text-teal-900'],
            cellRenderer: NumberCellRenderer,
            field: 'twentile1To4',
            headerClass: ['bg-teal-800'],
            headerName: '1-4%',
            type: ['numericColumn'],
            width: 125,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-teal-300', 'text-teal-900'],
            cellRenderer: NumberCellRenderer,
            field: 'twentile5To20',
            headerClass: ['bg-teal-800'],
            headerName: '5-20%',
            type: ['numericColumn'],
            width: 125,
          },
        ],
        headerClass: ['bg-teal-900'],
        headerGroupComponentParams: {
          icon: 'fa-ranking-star',
        },
        headerName: 'Modeling',
      },
      {
        children: [
          {
            aggFunc: 'sum',
            cellClass: ['bg-gray-200', 'text-gray-900'],
            cellRenderer: NumberCellRenderer,
            field: 'nth',
            headerClass: ['bg-gray-800'],
            headerName: 'N-th',
            type: ['numericColumn'],
            width: 125,
          },
        ],
        headerClass: ['bg-gray-900'],
        headerName: '',
      },
      {
        children: [
          {
            aggFunc: 'sum',
            cellClass: ['bg-green-300', 'text-green-900'],
            cellRenderer: NumberCellRenderer,
            field: 'quantityFinalOutput',
            headerClass: ['bg-green-800'],
            headerName: 'Count',
            type: ['numericColumn'],
            width: 125,
          },
          {
            cellClass: ['bg-green-400', 'text-green-900'],
            cellRenderer: PercentCellRenderer,
            cellRendererParams: (params: PercentCellProps<MailingSummary, number>) => {
              return {
                ...params,
                grandTotalFields: ['quantityReceived'],
                totalFields: ['quantityFinalOutput'],
              } as PercentCellProps<MailingSummary, number>;
            },
            field: 'percentFinalOutput',
            filter: 'agNumberColumnFilter',
            headerClass: ['bg-green-800'],
            headerName: '%',
            type: ['numericColumn'],
            width: 125,
          },
        ],
        headerClass: ['bg-green-900'],
        headerName: 'Final',
      },
    ] as ColGroupDef<MailingSummary>[];
  }, []);
};
